import AlertMgr from './../../custom-api/alert-custom-api'
import {ShopperLogin} from 'commerce-sdk-isomorphic'
import GCProcessingMgr from '../../custom-api/gc-processing-custom-api'
import OrderMonitorMgr from '../../custom-api/order-monitor-custom-api'
import {REFUND_BUSINESS_RESOLUTION_STEPS} from '../../util/constants'
import {NotificationRequestItem} from '@adyen/api-library/lib/src/typings/notification/notificationRequestItem'

/**
 * Sends an alert for Adyen-related events, provided a multi-site configuration object is available.
 *
 * @async
 * @param {string} step - The identifier for the specific step or process in the Adyen workflow.
 * @param {string} message - The message to be included in the alert.
 * @param {Object} multiSiteObj - The multi-site configuration object containing site-specific details.
 * @param {string} multiSiteObj.siteId - The ID of the site for which the alert is being sent.
 * @param {string} multiSiteObj.orgId - The organization ID associated with the site.
 * @param {string} multiSiteObj.locale - The locale setting for the site.
 * @returns {Promise<void>} - A promise that resolves when the alert has been sent or rejects if an error occurs.
 *
 * @throws Will log an error message to the console if sending the alert fails.
 */
export const AdyenAlert = async (step, message, multiSiteObj) => {
    try {
        // Check if the multiSiteObj parameter is provided. If not - do not send alert.
        if (!multiSiteObj) {
            return
        }
        let accessToken = await getAccessToken(multiSiteObj)
        let alertMgr = new AlertMgr({
            parameters: {
                siteId: multiSiteObj.siteId,
                organizationId: multiSiteObj.orgId,
                locale: multiSiteObj.locale
            }
        })

        let context = {
            siteId: multiSiteObj.siteId,
            message: message,
            category: 'Adyen',
            order: '',
            page: '',
            job: '',
            asset: '',
            identifier: step,
            fileName: '',
            lineNumber: ''
        }

        if (multiSiteObj.orderNo) {
            context.identifier = context.identifier + ' - ' + multiSiteObj.orderNo
            context.orderNo = multiSiteObj.orderNo
        }
        if (multiSiteObj.notification) {
            context.notification = JSON.stringify(multiSiteObj.notification)
        }
        if (multiSiteObj.request) {
            context.request = JSON.stringify(multiSiteObj.request)
        }
        if (multiSiteObj.answer) {
            context.answer = JSON.stringify(multiSiteObj.answer)
        }

        await alertMgr.sendAlert(
            {
                context: context,
                alertType: 'technical',
                logging: 'yes'
            },
            accessToken
        )
    } catch (e) {
        console.error('Error sending Adyen alert:', e)
    }
}


/**
 * Sends a business alert notification when a capture operation fails, including detailed resolution steps.
 *
 * @async
 * @function CaptureAlert
 * @param {Object} alertObj - The alert configuration object containing siteId, orgId, and locale.
 * @param {Object} notification - The notification object containing refund details, including reason and merchantReference.
 * @param {string} orderNo - The order number associated with the refund, used as a fallback if merchantReference is unavailable.
 * @returns {Promise<void>} Resolves when the alert has been sent or logs an error if the process fails.
 */
export const CaptureAlert = async (alertObj, notification, orderNo) => {
    try {
        const accessToken = await getAccessToken(alertObj)
        const orderID = notification.merchantReference || orderNo

        const captureFailedMessage = `Capture ${
            orderID ? `for order number ` + orderID : ''
        } has failed for the following reason: ${notification.reason}<br> <br>${REFUND_BUSINESS_RESOLUTION_STEPS.join(
            '<br>'
        )}`

        await new AlertMgr({
            parameters: {
                siteId: alertObj.siteId,
                organizationId: alertObj.orgId,
                locale: alertObj.locale
            }
        }).sendAlert(
            {
                context: {
                    siteId: alertObj.siteId,
                    message: captureFailedMessage,
                    category: 'Adyen',
                    order: notification.merchantReference || orderNo || '',
                    page: '',
                    job: '',
                    asset: '',
                    identifier: notification.merchantReference || orderNo || '',
                    fileName: '',
                    lineNumber: '',
                    stack: '',
                    notification: notification ? JSON.stringify(notification) : ''
                },
                alertType: 'technical',
                logging: 'yes'
            },
            accessToken
        )
    } catch (e) {
        console.error('Error sending Capture alert:', e)
    }
}

/**
 * Sends a business alert notification when a refund operation fails, including detailed resolution steps.
 *
 * @async
 * @function RefundAlert
 * @param {Object} alertObj - The alert configuration object containing siteId, orgId, and locale.
 * @param {Object} notification - The notification object containing refund details, including reason and merchantReference.
 * @param {string} orderNo - The order number associated with the refund, used as a fallback if merchantReference is unavailable.
 * @returns {Promise<void>} Resolves when the alert has been sent or logs an error if the process fails.
 */
export const RefundAlert = async (alertObj, notification, orderNo) => {
    try {
        const accessToken = await getAccessToken(alertObj)

        const updatedReason =
            notification.reason === 'Transaction not found'
                ? 'Payment transaction not found'
                : notification.reason

        const orderID = notification.merchantReference || orderNo

        const refundFailedMessage = `Refund ${
            orderID ? `for order number ` + orderID : ''
        } has failed for the following reason: ${updatedReason}<br> <br>${REFUND_BUSINESS_RESOLUTION_STEPS.join(
            '<br>'
        )}`

        await new AlertMgr({
            parameters: {
                siteId: alertObj.siteId,
                organizationId: alertObj.orgId,
                locale: alertObj.locale
            }
        }).sendAlert(
            {
                context: {
                    siteId: alertObj.siteId,
                    message: refundFailedMessage,
                    category: 'Refunds',
                    order: notification.merchantReference || orderNo || '',
                    page: '',
                    job: '',
                    asset: '',
                    identifier: notification.merchantReference || orderNo || '',
                    fileName: '',
                    lineNumber: '',
                    stack: '',
                    notification: notification ? JSON.stringify(notification) : ''
                },
                alertType: notification ? 'business' : 'technical',
                logging: notification ? 'no' : 'yes'
            },
            accessToken
        )
    } catch (e) {
        console.error('Error sending Refund alert:', e)
    }
}

/**
 * Asynchronously retrieves an access token for the specified site using client credentials.
 *
 * This function utilizes the ShopperLogin API to obtain an access token required for
 * authenticating requests to the Commerce API. It constructs the necessary authorization
 * headers using the client ID and secret, and configures the request with site-specific
 * parameters.
 *
 * @param {Object} multiSiteObj - An object containing site-specific information.
 * @param {string} multiSiteObj.siteId - The unique identifier for the site.
 * @returns {Promise<string>} A promise that resolves to the access token string.
 *
 * @throws {Error} Throws an error if the access token retrieval fails.
 *
 * @example
 * const multiSiteObj = { siteId: 'site123' };
 * getAccessToken(multiSiteObj)
 *     .then(token => {
 *         console.log('Access Token:', token);
 *     })
 *     .catch(error => {
 *         console.error('Error retrieving access token:', error);
 *     });
 */
export const getAccessToken = async (multiSiteObj) => {
    const CLIENT_ID = process.env.COMMERCE_API_CLIENT_ID
    const SECRET = process.env.PWA_KIT_SLAS_CLIENT_SECRET
    const basicToken = `${CLIENT_ID}:${SECRET}`
    const base64data = Buffer.from(basicToken).toString('base64')
    const headers = {
        Authorization: `Basic ${base64data}`
    }

    const config = {
        parameters: {
            clientId: CLIENT_ID,
            organizationId: process.env.COMMERCE_API_ORG_ID,
            shortCode: process.env.COMMERCE_API_SHORT_CODE,
            siteId: multiSiteObj.siteId
        },
        headers: headers
    }

    const loginClient = new ShopperLogin(config)
    const response = await loginClient.getAccessToken({
        headers,
        body: {grant_type: 'client_credentials', channel_id: multiSiteObj.siteId}
    })

    let accessToken = response.access_token

    return accessToken
}

/**
 * Determines if any shipment in the basket uses an express shipping method.
 *
 * This function checks each shipment within the provided basket to see if its
 * shipping method ID contains the keywords 'express' or 'dayair', indicating
 * an express shipping method. If any shipment meets this criterion, the function
 * returns true; otherwise, it returns false.
 *
 * @param {Object} order - The basket object containing shipments to be evaluated.
 * @param {Array} order.shipments - An array of shipment objects within the basket.
 * @param {Object} basket.shipments[].shippingMethod - The shipping method associated with a shipment.
 * @param {string} basket.shipments[].shippingMethod.id - The ID of the shipping method.
 * @returns {boolean} - Returns true if any shipment uses an express shipping method; otherwise, false.
 */
export const isExpressShipping = (basket) => {
    if (!basket || !basket.shipments || basket.shipments.length === 0) {
        return false
    }

    let isExpress = false

    for (let i = 0; i < basket.shipments.length; i++) {
        const shippingMethodId = basket.shipments[i]?.shippingMethod?.id?.toLowerCase() ?? ''

        if (shippingMethodId.includes('express') || shippingMethodId.includes('dayair')) {
            isExpress = true
            break
        }
    }

    return isExpress
}

/**
 * Determines if a given basket qualifies as an omnichannel basket.
 * An omnichannel basket is defined by the presence of specific custom attributes
 * indicating various fulfillment options such as ship from store, click and collect today,
 * or at-home delivery today.
 *
 * @param {Object} basket - The basket object to evaluate. It may contain custom attributes
 *                          that define different fulfillment options.
 * @returns {boolean} - Returns true if the basket is considered omnichannel based on its
 *                      custom attributes; otherwise, returns false.
 */
export const isOmnichannelBasket = (basket) => {
    if (!basket) {
        return false
    }

    return (
        basket.c_shipFromStore ||
        basket.c_clickAndCollectToday ||
        basket.c_atHomeToday ||
        basket.c_clickAndCollectDate
    )
}

/**
 * Determines if the current order is an omnichannel order.
 *
 * An omnichannel order typically involves multiple channels of interaction,
 * such as online and in-store, and may include various fulfillment options.
 * This function checks the order's attributes to ascertain if it qualifies
 * as an omnichannel order.
 *
 * @returns {boolean} - Returns true if the order is an omnichannel order, false otherwise.
 */
export const isOmnichannelOrder = (order) => {
    if (!order) {
        return false
    }

    return (
        order.c_shipFromStore ||
        order.c_clickAndCollectToday ||
        order.c_atHomeToday ||
        order.c_clickAndCollectDate
    )
}

/**
 * Determines if a given payment method is a direct capture payment method.
 *
 * This function checks if the provided payment method string contains any of the
 * specific identifiers associated with direct capture payment methods, such as
 * 'bcmc', 'directEbanking', 'eps', 'ideal', 'alipay', or 'giropay'.
 *
 * @param {string} paymentMethod - The payment method identifier to be evaluated.
 * @returns {boolean} - Returns true if the payment method is a direct capture
 * payment method, otherwise returns false.
 */
export const isDirectCapturePayment = (paymentMethod) => {
    return (
        paymentMethod.indexOf('bcmc') > -1 ||
        paymentMethod.indexOf('directEbanking') > -1 ||
        paymentMethod.indexOf('eps') > -1 ||
        paymentMethod.indexOf('ideal') > -1 ||
        paymentMethod.indexOf('alipay') > -1
    )
}

/**
 * Processes gift cards for a specified order within a multi-site context.
 *
 * This function attempts to process gift cards by obtaining an access token
 * and utilizing the GCProcessingMgr to handle the gift card processing for
 * the given order ID. If the multiSiteObj is not provided, an error is thrown.
 *
 * @async
 * @param {Object} multiSiteObj - An object containing multi-site information.
 * @param {string} multiSiteObj.siteId - The site identifier for the current context.
 * @param {string} multiSiteObj.orgId - The organization identifier.
 * @param {string} multiSiteObj.locale - The locale setting for the site.
 * @param {string} orderId - The unique identifier for the order to process gift cards for.
 * @throws {Error} Throws an error if the multiSiteObj is not provided.
 * @returns {Promise<void>} A promise that resolves when the gift card processing is complete.
 */
export const processGiftCards = async (multiSiteObj, orderId) => {
    try {
        // Check if the multiSiteObj parameter is provided
        if (!multiSiteObj) {
            throw new Error('multiSiteObj is required')
        }

        let accessToken = await getAccessToken(multiSiteObj)
        await new GCProcessingMgr({
            parameters: {
                siteId: multiSiteObj.siteId,
                organizationId: multiSiteObj.orgId,
                locale: multiSiteObj.locale
            }
        }).processGiftCards(multiSiteObj.siteId, accessToken, {
            orderId: orderId
        })
    } catch (e) {
        console.error('Error processing gift certificates:', e)
    }
}

/**
 * Monitors an order alert for a given multi-site configuration and order ID.
 *
 * This function asynchronously monitors an order using the provided multi-site
 * configuration object and order ID. It retrieves an access token and utilizes
 * the OrderMonitorMgr to perform the monitoring operation. If the multiSiteObj
 * is not provided, an error is thrown. Any errors encountered during the process
 * are logged to the console.
 *
 * @async
 * @param {Object} multiSiteObj - The multi-site configuration object containing
 *                                necessary site details.
 * @param {string} multiSiteObj.siteId - The identifier for the site.
 * @param {string} multiSiteObj.orgId - The organization identifier.
 * @param {string} multiSiteObj.locale - The locale setting for the site.
 * @param {string} orderId - The unique identifier of the order to be monitored.
 * @throws {Error} Throws an error if the multiSiteObj is not provided.
 */
export const monitorOrderAlert = async (multiSiteObj, orderId) => {
    try {
        // Check if the multiSiteObj parameter is provided
        if (!multiSiteObj) {
            throw new Error('multiSiteObj is required')
        }

        const accessToken = await getAccessToken(multiSiteObj)
        const {siteId, orgId, locale} = multiSiteObj
        await new OrderMonitorMgr({
            parameters: {
                siteId: siteId,
                organizationId: orgId,
                locale: locale
            }
        }).monitorOrder(siteId, accessToken, {
            orderId: orderId
        })
    } catch (e) {
        console.error('Error monitoring order: ', e)
    }
}

/**
 * Checks if an authorization notification has already been processed to prevent duplicate handling.
 * @param {Object} notification - The notification object containing authorization details.
 * @returns {boolean} True if the authorization notification is a duplicate, false otherwise.
 */
export const isDuplicatedAuthorizationNotification = (order, notification) => {
    const pspReference = notification.pspReference
    const AUTHORISATION = NotificationRequestItem.EventCodeEnum.Authorisation.toString()

    const existingNotifications = order.c_adyen_notifications || []
    return existingNotifications.some((notif) => {
        const parsedNotif = JSON.parse(notif)
        return (
            parsedNotif.eventCode === AUTHORISATION &&
            parsedNotif.success === 'true' &&
            parsedNotif.pspReference === pspReference
        )
    })
}
